import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import toastHandler from 'Utils/toastHandler';
import { Mixpanel } from 'Utils/MixPanel';
import { IconButton } from '../../../components/IconButton';
import { aiChatImages } from '../../../Utils/images';
import { useSendFeedback } from '../../hooks';
import { AnswerContent } from '../AnswerContent';
import { useCopyToClipboard } from '../useCopyToClipboard';
import {
  Actions,
  Analysis,
  AnswerContainer,
  ErrorText,
  FeedbackForm,
  PrimaryButton
} from './styles';

export interface AiAnswerProps {
  content: string;
  threadId?: string;
  messageId?: string;
  animate?: boolean;
  pending?: boolean;
  isError?: boolean;
}

export const AiAnswer: FC<AiAnswerProps> = ({
  content,
  threadId,
  messageId,
  animate,
  pending,
  isError
}) => {
  const { t } = useTranslation('activities');
  const { copied, copy } = useCopyToClipboard();
  const [liked, setLiked] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [feedback, setFeedback] = useState('');
  const { mutateAsync: sendFeedback, isPending: isSendingFeedback } = useSendFeedback();

  if (pending && !content) {
    return (
      <AnswerContainer>
        <Analysis>
          <img src={aiChatImages.sparkles} alt="" />
          <span>{t('aiChat.analyzing')}</span>
        </Analysis>
      </AnswerContainer>
    );
  }

  if (isError) {
    return (
      <AnswerContainer>
        <ErrorText>{content}</ErrorText>
      </AnswerContainer>
    );
  }

  const canRate = Boolean(threadId && messageId) && !pending;

  const handleLike = () => {
    if (liked) return;
    setLiked(true);
    toastHandler.showSuccess(t('aiChat.thanksFeedback'));
    Mixpanel.track({ name: 'AI_POSITIVE_FEEDBACK', props: { threadId, messageId } });
  };

  const handleSendFeedback = async () => {
    if (!threadId || !messageId) return;

    const { success } = await sendFeedback({ comments: feedback, threadId, messageId });
    if (success) {
      setFeedback('');
      setIsFeedbackOpen(false);
      toastHandler.showSuccess(t('aiChat.feedbackSent'));
    } else {
      toastHandler.showError(t('messages:toasts.feedbackError'));
    }
  };

  return (
    <AnswerContainer>
      <AnswerContent content={content} animate={animate} />

      {!pending && (
        <Actions $visible={isFeedbackOpen}>
          <IconButton
            icon="copy"
            label={copied ? t('aiChat.copied') : t('aiChat.copy')}
            onClick={() => copy(content)}
          />
          {canRate && (
            <>
              <IconButton
                icon="thumbsUp"
                label={t('aiChat.like')}
                active={liked}
                onClick={handleLike}
              />
              <IconButton
                icon="thumbsDown"
                label={t('aiChat.dislike')}
                active={isFeedbackOpen}
                onClick={() => setIsFeedbackOpen((open) => !open)}
              />
            </>
          )}
        </Actions>
      )}

      {isFeedbackOpen && (
        <FeedbackForm>
          <textarea
            autoFocus
            value={feedback}
            placeholder={t('aiChat.feedbackPlaceholder')}
            onChange={(event) => setFeedback(event.target.value)}
          />
          <PrimaryButton
            type="button"
            disabled={!feedback.trim() || isSendingFeedback}
            onClick={handleSendFeedback}
          >
            {isSendingFeedback && (
              <Spin
                size="small"
                indicator={<LoadingOutlined style={{ fontSize: 12, color: 'inherit' }} />}
              />
            )}
            {isSendingFeedback ? t('aiChat.feedbackSending') : t('aiChat.sendFeedback')}
          </PrimaryButton>
        </FeedbackForm>
      )}
    </AnswerContainer>
  );
};
